import { makeAutoObservable, runInAction } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { changedFields } from "@/lib/changed-fields";
import type { RootStore } from "./root.store";
import type { ICustomRole, IWorkspaceMember } from "@/types/workspace";
import type { IPermissionCatalogue } from "@/types/permission-catalogue";

export class WorkspaceRolesStore {
  roles: ICustomRole[] = [];
  isLoading = false;
  activeRoleId: string | null = null;
  activeAssignmentMemberId: string | null = null;
  /** The permission catalogue for `catalogueTier` (NG-1272). Cached by tier
   *  rather than globally: the response's available/locked split is computed
   *  against a tier, so one held for the wrong tier would offer a Guest role
   *  the Administrator toggles. */
  catalogue: IPermissionCatalogue | null = null;
  catalogueTier: string | null = null;

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  get systemRoles(): ICustomRole[] {
    return this.roles.filter((role) => role.is_system);
  }

  get customRoles(): ICustomRole[] {
    return this.roles.filter((role) => !role.is_system);
  }

  /** Load the catalogue for a tier, reusing the one already held for it.
   *
   *  Errors are not swallowed: the caller renders a failure state, because a
   *  Permissions tab that silently shows nothing reads as "this role has no
   *  permissions", which is the opposite of the truth.
   */
  async fetchPermissionCatalogue(slug: string, tier: string): Promise<void> {
    if (this.catalogue && this.catalogueTier === tier) return;
    try {
      const catalogue = await this.rootStore.workspaceRoleService.getPermissionCatalogue(
        slug,
        tier,
      );
      runInAction(() => {
        this.catalogue = catalogue;
        this.catalogueTier = tier;
      });
    } catch (error) {
      console.error("Failed to load the permission catalogue", { slug, tier, error });
      runInAction(() => {
        this.catalogue = null;
        this.catalogueTier = null;
      });
      throw error;
    }
  }

  async fetchRoles(slug: string): Promise<ICustomRole[]> {
    this.isLoading = true;
    try {
      const roles = await this.rootStore.workspaceRoleService.listRoles(slug);
      runInAction(() => {
        this.roles = roles;
      });
      return roles;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  /**
   * Re-read the whole list after a write, because `is_default` is a verdict about the
   * *workspace*, not a property of one row (NG-1335).
   *
   * Marking one role the default clears every other, and clearing the last claim hands
   * the badge back to the built-in Standard User. A single-role response cannot carry
   * that answer, so splicing it into the cached array leaves the "Default" badge on a
   * role that no longer holds it — or on no role at all — until the page is reloaded,
   * which is the exact symptom NG-1335 is about.
   *
   * Deliberately leaves `isLoading` alone: the table is already on screen and flipping
   * it would flash a skeleton over a change the user just made.
   *
   * Never throws. The write already succeeded, so failing the caller here would toast
   * an error over a save that worked; a stale badge until the next load is the milder
   * failure.
   */
  private async refreshRolesAfterWrite(slug: string): Promise<void> {
    try {
      const roles = await this.rootStore.workspaceRoleService.listRoles(slug);
      runInAction(() => {
        this.roles = roles;
      });
    } catch (error) {
      console.warn("Failed to refresh roles after a role write; the Default badge may be stale", {
        slug,
        error,
      });
    }
  }

  async createRole(
    slug: string,
    data: Partial<ICustomRole>,
    source: AnalyticsSource = "page",
  ): Promise<ICustomRole> {
    this.activeRoleId = "creating";
    try {
      const role = await this.rootStore.workspaceRoleService.createRole(slug, data);
      runInAction(() => {
        this.roles = [...this.roles, role];
      });
      // Before the refresh, so a failed reload doesn't drop it (NG-2063).
      track("role_created", { role_id: role.id, source });
      await this.refreshRolesAfterWrite(slug);
      return role;
    } finally {
      runInAction(() => {
        this.activeRoleId = null;
      });
    }
  }

  /** NG-1901: a server-side copy of `roleId`. Same bookkeeping as
   *  `createRole` — the copy is appended, then the list re-read so every row's
   *  `can_delete` reflects one more role in the workspace. */
  async duplicateRole(
    slug: string,
    roleId: string,
    source: AnalyticsSource = "page",
  ): Promise<ICustomRole> {
    this.activeRoleId = "creating";
    try {
      const role = await this.rootStore.workspaceRoleService.duplicateRole(slug, roleId);
      runInAction(() => {
        this.roles = [...this.roles, role];
      });
      track("role_created", { role_id: role.id, duplicated_from: roleId, source });
      await this.refreshRolesAfterWrite(slug);
      return role;
    } finally {
      runInAction(() => {
        this.activeRoleId = null;
      });
    }
  }

  async updateRole(
    slug: string,
    roleId: string,
    data: Partial<ICustomRole>,
    source: AnalyticsSource = "page",
  ): Promise<ICustomRole> {
    this.activeRoleId = roleId;
    try {
      // Legacy flag-off editor still has a Save action. Make that action an
      // explicit publish, not a direct edit of the RBAC row: the same API
      // boundary protects old bundles and the flagged draft editor alike.
      await this.rootStore.workspaceRoleService.updateRoleDraft(slug, roleId, data);
      await this.rootStore.workspaceRoleService.publishRoleDraft(slug, roleId);
      const previous = this.roles.find((item) => item.id === roleId);
      track("role_updated", { role_id: roleId, fields: changedFields(data, previous), source });
      await this.refreshRolesAfterWrite(slug);
      const role = this.roles.find((item) => item.id === roleId);
      if (!role && !previous) throw new Error("Published role not found");
      return role ?? previous!;
    } finally {
      runInAction(() => {
        this.activeRoleId = null;
      });
    }
  }

  /** Make *roleId* the workspace's default role (NG-1902).
   *
   *  Goes through the badge-only route, never `updateRole`: that one publishes
   *  the role's draft, which would take an admin's unpublished edits live with
   *  it. Not optimistic, deliberately: `is_default` is a workspace verdict the
   *  server resolves (`resolve_default_role`), so the badge moves when the
   *  response says it landed, and its moving is the sign the write persisted.
   *  One holder only, so the response row replaces its own and every other row
   *  drops the badge; no re-list needed. A failure rejects for the caller's
   *  toast. */
  async setDefaultRole(
    slug: string,
    roleId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const updated = await this.rootStore.workspaceRoleService.setDefaultRole(slug, roleId);
    track("role_updated", { role_id: roleId, fields: ["is_default"], source });
    runInAction(() => {
      this.roles = this.roles.map((role) =>
        role.id === updated.id ? updated : { ...role, is_default: false },
      );
    });
  }

  async deleteRole(slug: string, roleId: string, source: AnalyticsSource = "page"): Promise<void> {
    this.activeRoleId = roleId;
    try {
      await this.rootStore.workspaceRoleService.deleteRole(slug, roleId);
      track("role_deleted", { role_id: roleId, source });
      runInAction(() => {
        this.roles = this.roles.filter((role) => role.id !== roleId);
      });
      await this.refreshRolesAfterWrite(slug);
    } finally {
      runInAction(() => {
        this.activeRoleId = null;
      });
    }
  }

  async assignRole(
    slug: string,
    memberId: string,
    userId: string,
    customRoleId: string | null,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.activeAssignmentMemberId = memberId;
    try {
      const response = await this.rootStore.workspaceRoleService.assignWorkspaceRole(
        slug,
        userId,
        customRoleId,
      );
      // The same signal a role change from the members table sends.
      track("workspace_member_updated", { member_id: memberId, fields: ["role"], source });
      const members = this.rootStore.workspaceMembers;
      runInAction(() => {
        if ("member" in response) {
          members.replaceMember(response);
        } else {
          const patch: Partial<IWorkspaceMember> = {};
          if (response.role !== undefined && response.role !== null) {
            patch.role = response.role;
          }
          members.patchMember(memberId, patch);
        }
      });
    } finally {
      runInAction(() => {
        this.activeAssignmentMemberId = null;
      });
    }
  }
}
